import * as React from "react";
import { Breadcrumb, BreadcrumbItem, Button, DataList, Icon, PageBreadcrumb, PageGroup, PageSection } from "@patternfly/react-core";
import { Toolbar, ToolbarContent, ToolbarGroup, ToolbarItem, Tooltip } from "@patternfly/react-core";
import { PlusIcon, SyncIcon, TagIcon } from "@patternfly/react-icons";

import { get } from "../../api/api";
import { localLog } from "../../utils/Utilities";
import { OrganizationContext } from "../../common/contexts/OrganizationContext";
import { ErrorStateComponent } from "../../components/ErrorStateComponent";
import { LoadingStateComponent } from "../../components/LoadingStateComponent";
import { NoResultsFoundComponent } from "../../components/NoResultsFoundComponent";
import { CategoryDataListItem } from "./CategoryDataListItem";
import { CategoryModal } from "./CategoryModal";
import { PageHeadingComponent } from "../../components/PageHeadingComponent";

export const Categories = () => {

  const organization = React.useContext(OrganizationContext);

  const [action, setAction] = React.useState("create");
  const [debug] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [category, setCategory] = React.useState(null);
  const [categories, setCategories] = React.useState([]);
  const [isActionOpen, setIsActionOpen] = React.useState(0);
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const fetchCategories = async () => {
    setError(null);
    setLoading(true);
    try {
      const response = await get(organization.id, `/shoutoutcategory/getCategories?organizationId=${organization.id}`);
      if (debug) localLog("response: " + JSON.stringify(response, null, 2));
      setCategories(response.data);
      setLoading(false);
    } catch (error) {
      console.error(JSON.stringify(error));
      setError(error);
      setLoading(false);
    }
  };

  React.useEffect(() => {

    const firstFetch = async() => {
      fetchCategories();
    }

    if (organization?.id) {
      firstFetch();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization.id]);

  const onActionSelect = () => {
    setIsActionOpen(0);
  };

  const onActionToggle = (id) => {
    if (isActionOpen === id) {
      setIsActionOpen(0);
    } else {
      setIsActionOpen(id);
    }
  };

  const onClose = () => {
    onModalToggle();
  };

  const onCreateCategory = () => {
    setCategory(null);
    setAction("create");
    onModalToggle();
  };

  const onDeleteCategory = (id) => {
    setCategory(categories.find(item => item.id === id));
    setAction("delete");
    onModalToggle();
  };

  const onUpdateCategory = (id) => {
    setCategory(categories.find(item => item.id === id));
    setAction("update");
    onModalToggle();
  };

  const onModalToggle = (e) => {
    setIsModalOpen(!isModalOpen);
  };

  const onRefresh = () => {
    if (isModalOpen)
      setIsModalOpen(false);
    fetchCategories();
  };

  const CategoriesToolbar = (
    <Toolbar>
      <ToolbarContent>
        <ToolbarGroup>
          <ToolbarItem variant="label">
            Categories
          </ToolbarItem>
        </ToolbarGroup>
        <ToolbarGroup align={{default: 'alignRight' }}>
          <ToolbarItem variant="separator" />
          <ToolbarItem>
            <Tooltip content="Create new Category">
              <Button variant="plain" onClick={onCreateCategory}><Icon><PlusIcon /></Icon></Button>
            </Tooltip>
          </ToolbarItem>
          <ToolbarItem>
            <Tooltip content="Refresh List">
              <Button variant="plain" onClick={onRefresh}><Icon><SyncIcon /></Icon></Button>
            </Tooltip>
          </ToolbarItem>
        </ToolbarGroup>
      </ToolbarContent>
    </Toolbar>
  );

  return (
    <React.Fragment>
      <PageGroup stickyOnBreakpoint={{ default: 'top' }}>
        <PageBreadcrumb>
          <Breadcrumb>
            <BreadcrumbItem to="/">Dashboard</BreadcrumbItem>
            <BreadcrumbItem isActive>Administration</BreadcrumbItem>
            <BreadcrumbItem isActive>Categories</BreadcrumbItem>
          </Breadcrumb>
        </PageBreadcrumb>
        <PageHeadingComponent title="Categories" subtitle="Administer the categories used in the application." icon={<TagIcon />} />
      </PageGroup>
      <PageSection>
        {
          loading
          ? <LoadingStateComponent />
          : error
            ? <ErrorStateComponent error={error} />
            : <>
                {
                  CategoriesToolbar
                }
                {
                  categories.length === 0
                  ? <NoResultsFoundComponent isFullHeight={false} title="No Categories Found" message="There are currently no categories defined for the application." />
                  : <DataList isCompact>
                      {
                        categories.map((item) => (
                          <CategoryDataListItem
                            key={item.id}
                            category={item}
                            isOpen={isActionOpen}
                            setIsOpen={setIsActionOpen}
                            onSelect={onActionSelect}
                            onToggle={onActionToggle}
                            onUpdate={onUpdateCategory}
                            onDelete={onDeleteCategory}
                          />
                        ))
                      }
                    </DataList>
                }
              </>
        }
      </PageSection>
      <CategoryModal
        isOpen={isModalOpen}
        onClose={onClose}
        category={category}
        organizationId={organization.id}
        action={action}
        onRefresh={onRefresh}
      />
    </React.Fragment>
  );

};
